import './App.css';
import './theme/global.css';
import { blur } from './theme/tokens/blur';
import { motion } from './theme/tokens/motion';
import { radius } from './theme/tokens/radius';
import React from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import MainLayout from './components/Layout/MainLayout';
import Home from './pages/Home';
import Shows from './pages/Shows';
import Bands from './pages/Bands';
import Musicians from './pages/Musicians';
import Venues from './pages/Venues';
import MusicianProfile from './pages/MusicianProfile';
import FanProfile from './pages/FanProfile';
import UserProfile from './pages/UserProfile';
import VenueProfile from './pages/VenueProfile';
import BandProfile from './pages/BandProfile';
import Show from './pages/Show';
import VenueQR from './pages/VenueQR';
import ShowQR from './pages/ShowQR';
import MusicianQR from './pages/MusicianQR';
import BandQR from './pages/BandQR';
import Chat from './pages/Chat';
import SystemTest from './pages/SystemTest';
import Loading from './pages/Loading';
import SignUp from './pages/SignUp';
import SignIn from './pages/SignIn';
import AddBand from './pages/AddBand';
import AddShow from './pages/AddShow';
import EditBandProfile from './pages/EditBandProfile';
import EditMusicianProfile from './pages/EditMusicianProfile';
import EditFanProfile from './pages/EditFanProfile';
import About from './pages/About';
import Contact from './pages/Contact';
import Feedback from './pages/Feedback';
import Connections from './pages/Connections';
import UserManagement from './pages/admin/UserManagement';
import BandDetail from './pages/nested/BandDetail';
import MusicianDetail from './pages/nested/MusicianDetail';
import MusicianLinks from './pages/MusicianLinks';
import VenueDetail from './pages/nested/VenueDetail';
import ShowDetail from './pages/nested/ShowDetail';
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';
import theme from './theme';

const router = createBrowserRouter([
  {
    path: "/loading",
    element: <Loading />,
  },
  {
    path: "/",
    element: <MainLayout />,
    children: [
      { index: true, element: <Navigate to="/home" replace /> },
      {
        path: "home",
        element: <Home />,
        handle: {
          headerConfig: {
            showGradient: false,
            showBack: false,
            showLogo: false,
            showMore: false,
            showSearch: true,
            showHamburger: true
          }
        }
      },
      {
        path: "shows",
        element: <Shows />,
        handle: {
          headerConfig: {
            showGradient: true,
            showBack: true,
            showLogo: false,
            showMore: false,
            showSearch: true,
            showHamburger: false
          }
        }
      },
      {
        path: "show",
        element: <Show />,
        handle: {
          headerConfig: {
            showGradient: true,
            showBack: true,
            showLogo: true,
            showMore: true,
            showSearch: false,
            showHamburger: false
          }
        }
      },
      {
        path: "venue/:id/qr",
        element: <VenueQR />,
        handle: {
          headerConfig: {
            showGradient: true,
            showBack: true,
            showLogo: true,
            showMore: false,
            showSearch: false,
            showHamburger: false
          }
        }
      },
      {
        path: "show/:id/qr",
        element: <ShowQR />,
        handle: {
          headerConfig: {
            showGradient: true,
            showBack: true,
            showLogo: true,
            showMore: false,
            showSearch: false,
            showHamburger: false
          },
          showFooter: false
        }
      },
      {
        path: "musician/:id/qr",
        element: <MusicianQR />,
        handle: {
          headerConfig: {
            showGradient: true,
            showBack: true,
            showLogo: true,
            showMore: false,
            showSearch: false,
            showHamburger: false
          },
          showFooter: false
        }
      },
      {
        path: "band/:id/qr",
        element: <BandQR />,
        handle: {
          headerConfig: {
            showGradient: true,
            showBack: true,
            showLogo: true,
            showMore: false,
            showSearch: false,
            showHamburger: false
          },
          showFooter: false
        }
      },
      {
        path: "bands",
        element: <Bands />,
        handle: {
          headerConfig: {
            showGradient: true,
            showBack: true,
            showLogo: false,
            showMore: false,
            showSearch: true,
            showHamburger: false
          }
        }
      },
      {
        path: "musicians",
        element: <Musicians />,
        handle: {
          headerConfig: {
            showGradient: true,
            showBack: true,
            showLogo: false,
            showMore: false,
            showSearch: true,
            showHamburger: false
          }
        }
      },
      {
        path: "venues",
        element: <Venues />,
        handle: {
          headerConfig: {
            showGradient: true,
            showBack: true,
            showLogo: false,
            showMore: false,
            showSearch: true,
            showHamburger: false
          }
        }
      },
      {
        path: "profile",
        element: <UserProfile />,
        handle: {
          headerConfig: {
            showGradient: true,
            showBack: false,
            showLogo: true,
            showMore: false,  // Bug 44: Remove ellipsis from account page
            showSearch: false,
            showHamburger: true
          }
        }
      },
      {
        path: "musician-profile",
        element: <MusicianProfile />,
        handle: {
          headerConfig: {
            showGradient: true,
            showBack: false,
            showLogo: true,
            showMore: true,
            showSearch: false,
            showHamburger: true
          }
        }
      },
      {
        path: "fan-profile",
        element: <FanProfile />,
        handle: {
          headerConfig: {
            showGradient: true,
            showBack: true,
            showLogo: true,
            showMore: true,
            showSearch: false,
            showHamburger: false
          }
        }
      },
      {
        path: "fans/:id",
        element: <FanProfile />,
        handle: {
          headerConfig: {
            showGradient: true,
            showBack: true,
            showLogo: true,
            showMore: true,
            showSearch: false,
            showHamburger: false
          }
        }
      },
      {
        path: "venue-profile",
        element: <VenueProfile />,
        handle: {
          headerConfig: {
            showGradient: true,
            showBack: true,
            showLogo: true,
            showMore: true,
            showSearch: false,
            showHamburger: false
          }
        }
      },
      {
        path: "band-profile",
        element: <BandProfile />,
        handle: {
          headerConfig: {
            showGradient: true,
            showBack: true,
            showLogo: true,
            showMore: true,
            showSearch: false,
            showHamburger: false
          }
        }
      },
      {
        path: "bands/:id",
        element: <BandDetail />,
        handle: {
          headerConfig: {
            showGradient: true,
            showBack: true,
            showLogo: true,
            showMore: true,
            showSearch: false,
            showHamburger: false
          }
        }
      },
      {
        path: "musicians/:id",
        element: <MusicianDetail />,
        handle: {
          headerConfig: {
            showGradient: true,
            showBack: true,
            showLogo: true,
            showMore: true,
            showSearch: false,
            showHamburger: false
          }
        }
      },
      {
        path: "musicians/:id/links",
        element: <MusicianLinks />,
        handle: {
          headerConfig: {
            showGradient: true,
            showBack: true,
            showLogo: true,
            showMore: true,
            showSearch: false,
            showHamburger: false
          },
          showFooter: false
        }
      },
      {
        path: "venues/:id",
        element: <VenueDetail />,
        handle: {
          headerConfig: {
            showGradient: true,
            showBack: true,
            showLogo: true,
            showMore: true,
            showSearch: false,
            showHamburger: false
          }
        }
      },
      {
        path: "shows/:id",
        element: <ShowDetail />,
        handle: {
          headerConfig: {
            showGradient: true,
            showBack: true,
            showLogo: true,
            showMore: true,
            showSearch: false,
            showHamburger: false
          }
        }
      },
      {
        path: "about",
        element: <About />,
        handle: {
          headerConfig: {
            showGradient: true,
            showBack: true,
            showLogo: true,
            showMore: false,
            showSearch: false,
            showHamburger: false
          }
        }
      },
      {
        path: "contact",
        element: <Contact />,
        handle: {
          headerConfig: {
            showGradient: true,
            showBack: true,
            showLogo: true,
            showMore: false,
            showSearch: false,
            showHamburger: false
          }
        }
      },
      {
        path: "feedback",
        element: (
          <ProtectedRoute>
            <Feedback />
          </ProtectedRoute>
        ),
        handle: {
          headerConfig: {
            showGradient: true,
            showBack: true,
            showLogo: true,
            showMore: false,
            showSearch: false,
            showHamburger: false
          }
        }
      },
      {
        path: "chat",
        element: <Chat />,
        handle: {
          headerConfig: {
            showGradient: true,
            showBack: false,
            showLogo: true,
            showMore: false,
            showSearch: false,
            showHamburger: true
          }
        }
      },
      {
        path: "connections",
        element: <Connections />,
        handle: {
          headerConfig: {
            showGradient: false,
            showBack: true,
            showLogo: false,
            showMore: false,
            showSearch: true,
            showHamburger: false
          }
        }
      },
      {
        path: "system-test",
        element: <SystemTest />,
        handle: {
          headerConfig: {
            showGradient: true,
            showBack: false,
            showLogo: true,
            showMore: true,
            showSearch: true,
            showHamburger: true
          }
        }
      },
      {
        path: "signup",
        element: <SignUp />,
        handle: {
          headerConfig: {
            showGradient: true,
            showBack: true,
            showLogo: true,
            showMore: false,
            showSearch: false,
            showHamburger: false
          }
        }
      },
      {
        path: "signin",
        element: <SignIn />,
        handle: {
          headerConfig: {
            showGradient: true,
            showBack: true,
            showLogo: true,
            showMore: false,
            showSearch: false,
            showHamburger: false
          }
        }
      },
      {
        path: "add-band",
        element: (
          <ProtectedRoute>
            <AddBand />
          </ProtectedRoute>
        ),
        handle: {
          headerConfig: {
            showGradient: true,
            showBack: true,
            showLogo: true,
            showMore: false,
            showSearch: false,
            showHamburger: false
          }
        }
      },
      {
        path: "add-show",
        element: (
          <ProtectedRoute>
            <AddShow />
          </ProtectedRoute>
        ),
        handle: {
          headerConfig: {
            showGradient: true,
            showBack: true,
            showLogo: true,
            showMore: false,
            showSearch: false,
            showHamburger: false
          }
        }
      },
      {
        path: "edit-band-profile",
        element: (
          <ProtectedRoute>
            <EditBandProfile />
          </ProtectedRoute>
        ),
        handle: {
          headerConfig: {
            showGradient: true,
            showBack: true,
            showLogo: true,
            showMore: false,
            showSearch: false,
            showHamburger: false
          },
          showFooter: false
        }
      },
      {
        // Real entry point: BandDetail.tsx's Edit menu item navigates here
        // with a real band id. The no-id route above stays for BandProfile.tsx's
        // (still-mock) Edit Profile link - EditBandProfile shows a "No Band
        // Selected" state without one.
        path: "edit-band-profile/:id",
        element: (
          <ProtectedRoute>
            <EditBandProfile />
          </ProtectedRoute>
        ),
        handle: {
          headerConfig: {
            showGradient: true,
            showBack: true,
            showLogo: true,
            showMore: false,
            showSearch: false,
            showHamburger: false
          },
          showFooter: false
        }
      },
      {
        path: "edit-musician-profile",
        element: (
          <ProtectedRoute>
            <EditMusicianProfile />
          </ProtectedRoute>
        ),
        handle: {
          headerConfig: {
            showGradient: true,
            showBack: true,
            showLogo: true,
            showMore: false,
            showSearch: false,
            showHamburger: false
          },
          showFooter: false
        }
      },
      {
        // Real entry point: MusicianDetail.tsx's Edit menu item navigates here
        // with a real musician id. The no-id route above stays for the legacy
        // self-only path (useMyMusician).
        path: "edit-musician-profile/:id",
        element: (
          <ProtectedRoute>
            <EditMusicianProfile />
          </ProtectedRoute>
        ),
        handle: {
          headerConfig: {
            showGradient: true,
            showBack: true,
            showLogo: true,
            showMore: false,
            showSearch: false,
            showHamburger: false
          },
          showFooter: false
        }
      },
      {
        path: "edit-fan-profile",
        element: (
          <ProtectedRoute>
            <EditFanProfile />
          </ProtectedRoute>
        ),
        handle: {
          headerConfig: {
            showGradient: true,
            showBack: true,
            showLogo: true,
            showMore: false,
            showSearch: false,
            showHamburger: false
          },
          showFooter: false
        }
      },
      {
        path: "admin/users",
        element: (
          <ProtectedRoute>
            <UserManagement />
          </ProtectedRoute>
        ),
        handle: {
          headerConfig: {
            showGradient: true,
            showBack: true,
            showLogo: true,
            showMore: false,
            showSearch: false,
            showHamburger: false
          }
        }
      },
    ]
  }
]);

function App() {
  return (
    <ConfigProvider
      theme={theme}
      alert={{
        styles: {
          title: { fontWeight: theme.token?.fontWeightStrong },
          root: {
            background: 'rgba(10, 132, 255, 0.8)',
            backdropFilter: `blur(${blur.sm})`,
            WebkitBackdropFilter: `blur(${blur.sm})`,
            border: 'none',
            borderRadius: radius.m,
          },
        },
      }}
    >
      <style>
        {`.ant-drawer-mask, .ant-modal-mask {
          backdrop-filter: blur(${blur.sm});
          -webkit-backdrop-filter: blur(${blur.sm});
        }

        /* Modal: dissolve (fade) motion using fast duration token */
        .ant-modal-root .ant-fade-enter,
        .ant-modal-root .ant-fade-appear,
        .ant-modal-root .ant-fade-leave {
          animation-duration: ${motion.durationFast}ms !important;
        }

        /* Drawer: slide-in/out from bottom (default placement) using fast duration token */
        .ant-drawer .ant-drawer-content-wrapper {
          transition-duration: ${motion.durationFast}ms !important;
          max-width: 768px !important;
          margin: 0 auto !important;
          border-top-left-radius: ${radius.xxl}px !important;
          border-top-right-radius: ${radius.xxl}px !important;
        }
        .ant-drawer .ant-drawer-content,
        .ant-drawer .ant-drawer-section {
          border-top-left-radius: ${radius.xxl}px !important;
          border-top-right-radius: ${radius.xxl}px !important;
        }
        .ant-drawer .ant-drawer-mask-motion-enter-active,
        .ant-drawer .ant-drawer-mask-motion-appear-active,
        .ant-drawer .ant-drawer-mask-motion-leave-active,
        .ant-drawer .ant-drawer-panel-motion-bottom-enter-active,
        .ant-drawer .ant-drawer-panel-motion-bottom-appear-active,
        .ant-drawer .ant-drawer-panel-motion-bottom-leave-active {
          transition-duration: ${motion.durationFast}ms !important;
        }`}
      </style>
      <RouterProvider router={router} />
    </ConfigProvider>
  );
}

export default App;
